// /**
//  * Orders details - list
//  */

// React
import React, {useState} from 'react';

// UI
import {
  createStyles,
  makeStyles,
  Theme,
  withStyles,
} from '@material-ui/core/styles';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  IconButton,
} from '@material-ui/core';

// Icon
import Icon from '@mdi/react';
import {
  mdiCheckBoxOutline,
  mdiChevronDown,
  mdiChevronUp,
  mdiClose,
  mdiSelectionMarker,
  mdiSquareOutline,
} from '@mdi/js';

// Helper
import {translate, checkMatchMedia} from '../../utils/helpers';

// Type
import {Item} from './interfaces';
import theme from '../../theme';

type Data = Item;
type Props = {
  setOpenUnits: (orderItem: Item) => void;
  locateItem: (inventoryItem: any) => void;
  data: any;
};
type Ord = 'asc' | 'desc';
interface HeadCell {
  id: keyof Data;
  label: string;
}
interface EnhancedTableProps {
  classes: ReturnType<typeof useStyles>;
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => void;
  order: Ord;
  orderBy: string;
  rowCount: number;
}

// Style
const StyledTableCell = withStyles((theme: Theme) =>
  createStyles({
    head: {
      backgroundColor: theme.palette.primary.main,
      whiteSpace: 'nowrap',
      color: theme.palette.common.white,
      '@media (max-width: 768px)': {
        fontSize: 10,
      },
    },
    body: {
      whiteSpace: 'nowrap',
      fontSize: 14,
    },
  })
)(TableCell);

const StyledTableRow = withStyles((theme: Theme) =>
  createStyles({
    root: {
      '&:nth-of-type(even)': {
        backgroundColor: theme.palette.action.hover,
      },
    },
  })
)(TableRow);
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
      '& .MuiTableCell-root': {
        padding: '5px',
      },
    },
    paper: {
      width: '100%',
    },
    parentRow: {
      '& > td': {
        fontWeight: 500,
        fontSize: '15px',
      },
    },
    table: {
      // minWidth: 750,
    },
    head: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
    },
    textArea: {
      width: '100%',
    },
    visuallyHidden: {
      border: 0,
      clip: 'rect(0 0 0 0)',
      height: 1,
      margin: -1,
      overflow: 'hidden',
      padding: 0,
      position: 'absolute',
      top: 20,
      width: 1,
    },
    container: {
      maxHeight: 540,
      borderRadius: '4px',
    },
    // Fully visible for active icons
    activeSortIcon: {
      opacity: 1,
      color: `${theme.palette.secondary.main}!important`,
    },
    // Half visible for inactive icons
    inactiveSortIcon: {
      opacity: 0.4,
    },
    // Cancel Dialog
    cancelIcon: {
      textAlign: 'center',
      position: 'relative',
      paddingLeft: theme.spacing(8),
      marginBottom: theme.spacing(7),
      '&::before': {
        content: '""',
        position: 'absolute',
        width: '100px',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        height: '100px',
        borderRadius: '50%',
        opacity: '0.2',
        backgroundColor: theme.palette.error.main,
      },
    },
    cancelTitle: {
      fontSize: '16px',
      fontWeight: 500,
      textAlign: 'center',
      marginBottom: theme.spacing(3),
    },
    actionsBox: {
      paddingTop: theme.spacing(2),
      paddingRight: theme.spacing(3),
      paddingLeft: theme.spacing(3),
    },
  })
);

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator<Key extends keyof any>(
  order: Ord,
  orderBy: Key
): (
  a: {[key in Key]: number | string},
  b: {[key in Key]: number | string}
) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(array: T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map(el => el[0]);
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const classes = useStyles();
  const {order, orderBy, onRequestSort} = props;
  const createSortHandler =
    (property: keyof Data) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };
  const headCells: HeadCell[] = [
    {id: 'status', label: 'وضعیت'},
    {id: 'productName', label: 'نام کالا'},
    {id: 'productId', label: 'شناسه کالا'},
    {id: 'variantId', label: 'شناسه تنوع کالا'},
    {id: 'productCode', label: 'کد کالا'},
    {id: 'variantCode', label: 'کد تنوع کالا'},
    {id: 'count', label: 'تعداد'},
    {id: 'serialNumber', label: 'سریال کالا'},
  ];

  return (
    <TableHead>
      <StyledTableRow>
        <StyledTableCell />
        {headCells.map(headCell => (
          <StyledTableCell
            key={headCell.id}
            align="center"
            sortDirection={orderBy === headCell.id ? order : false}
            data-cy={headCell.id}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
              classes={{
                // Override with the active class if this is the selected column or inactive otherwise
                icon:
                  orderBy === headCell.id
                    ? classes.activeSortIcon
                    : classes.inactiveSortIcon,
              }}
              IconComponent={() => (
                <Icon
                  path={
                    orderBy === headCell.id && order === 'desc'
                      ? mdiChevronDown
                      : mdiChevronUp
                  }
                  size={0.7}
                  className={
                    orderBy === headCell.id
                      ? classes.activeSortIcon
                      : classes.inactiveSortIcon
                  }
                />
              )}
            >
              {headCell.label}
            </TableSortLabel>
          </StyledTableCell>
        ))}
        <StyledTableCell />
      </StyledTableRow>
    </TableHead>
  );
}

export default function EnhancedTable(props: Props) {
  const classes = useStyles();
  const [order, setOrder] = React.useState<Ord>('desc');
  const [orderBy, setOrderBy] = React.useState<keyof Data>('productId');
  // const [dense] = React.useState(false);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  return (
    <div className={classes.root}>
      <Paper className={classes.paper}>
        <TableContainer className={classes.container}>
          <Table
            stickyHeader
            className={classes.table}
            aria-labelledby="tableTitle"
            size={checkMatchMedia('(max-width: 1024px)') ? 'small' : 'medium'}
            aria-label="enhanced table"
          >
            <EnhancedTableHead
              classes={classes}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              rowCount={props.data.length}
            />

            <TableBody>
              {stableSort(props.data, getComparator(order, orderBy))
                .slice()
                .map((row: any, index: number) => {
                  const labelId = `enhanced-table-checkbox-${index}`;
                  return (
                    <Row
                      key={index}
                      row={row}
                      index={index}
                      labelId={labelId}
                      setOpenUnits={() => props.setOpenUnits(row)}
                      locateItem={props.locateItem}
                    />
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </div>
  );
}

function Row(props: {
  row: any;
  labelId: string;
  index: number;
  setOpenUnits: () => void;
  locateItem: (inventoryItem: any) => void;
}) {
  const {row, labelId, index, setOpenUnits} = props;
  const [open, setOpen] = useState(true);
  const classes = useStyles();

  let variantName =
    row.type === 'PRODUCT' ? row.productName : translate(row.type);
  for (let i = 0; i <= 2 && row.featureAndValues[i]; i++) {
    variantName = variantName.concat(
      ' - ',
      row.featureAndValues[i].key,
      ' : ',
      row.featureAndValues[i].value
    );
  }

  if (row.sellerName || row.providerName) {
    const sellerName = row.sellerName ?? row.providerName;
    variantName = variantName.concat(' ( ', sellerName, ' )');
  }

  return (
    <>
      <StyledTableRow className={classes.parentRow} key={index} tabIndex={-1}>
        <TableCell>
          <IconButton
            aria-label="details"
            size="small"
            title="جمع آوری آیتم"
            data-cy="details"
            disabled={true}
            // onClick={() => setOpenUnits()}
            //disabled={props.operatorId === row.id}
          >
            <Icon
              path={
                row.status === 'PICKED' ? mdiCheckBoxOutline : mdiSquareOutline
              }
              size={1}
              color={
                row.status === 'PICKED' ? theme.palette.green.main : '#383836'
              }
            />
          </IconButton>
        </TableCell>
        <TableCell align="center" data-cy="row-id">
          {translate(row.status, 'order')}
        </TableCell>
        <TableCell id={labelId} scope="row" align="center">
          {variantName}
        </TableCell>
        <TableCell align="center" data-cy="row-id">
          {row.productId}
        </TableCell>
        <TableCell align="center" data-cy="row-id">
          {row.variantId}
        </TableCell>
        <TableCell align="center" scope="row" data-cy="row-id">
          {row.productCode}
        </TableCell>
        <TableCell align="center" scope="row" data-cy="row-id">
          {row.variantCode}
        </TableCell>
        <TableCell align="center" data-cy="row-id">
          {row.inventoryItems.length === 0
            ? row.amount
            : `(${(row.amount) - (row.amount - row.inventoryItems.length)}) ${row.amount} `}
        </TableCell>
        <TableCell align="center" data-cy="row-id">
          {row.serialNumber}
        </TableCell>
        <TableCell align="right">
          <IconButton
            aria-label="details"
            size="small"
            title="جمع آوری آیتم"
            data-cy="details"
            onClick={() => setOpenUnits()}
            //disabled={props.operatorId === row.id}
          >
            <Icon path={mdiSelectionMarker} size={1} color="#5c5c5c" />
          </IconButton>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
            disabled={row.inventoryItems.length === 0}
          >
            {open ? (
              <Icon
                path={mdiChevronUp}
                size={1}
                color={
                  row.inventoryItems.length === 0
                    ? '#d4d2da'
                    : theme.palette.secondary.main
                }
              />
            ) : (
              <Icon
                path={mdiChevronDown}
                size={1}
                color={
                  row.inventoryItems.length === 0
                    ? '#d4d2da'
                    : theme.palette.secondary.main
                }
              />
            )}
          </IconButton>
        </TableCell>
      </StyledTableRow>
      {open
        ? row.inventoryItems.map((inventoryItem: any) => {
            return (
              <StyledTableRow>
                <TableCell>
                  <IconButton
                    aria-label="details"
                    size="small"
                    title="جایگذاری آیتم"
                    data-cy="details"
                    disabled={true}
                  >
                    <Icon
                      path={
                        inventoryItem.status === 'PICKED'
                          ? mdiCheckBoxOutline
                          : mdiSquareOutline
                      }
                      size={1}
                      color={
                        inventoryItem.status === 'PICKED'
                          ? theme.palette.green.main
                          : '#383836'
                      }
                    />
                  </IconButton>
                </TableCell>
                <TableCell align="center" data-cy="row-id">
                  {translate(inventoryItem.status, 'inventory')}
                </TableCell>
                <TableCell scope="row" align="center">
                  {row.type === 'PRODUCT'
                    ? row.productName
                    : translate(row.type, 'inventory')}
                </TableCell>
                <TableCell align="center" data-cy="row-id">
                  {row.productId}
                </TableCell>
                <TableCell align="center" data-cy="row-id">
                  {row.variantId}
                </TableCell>
                <TableCell align="center" scope="row" data-cy="row-id">
                  {row.productCode}
                </TableCell>
                <TableCell align="center" scope="row" data-cy="row-id">
                  {row.variantCode}
                </TableCell>
                <TableCell align="center" data-cy="row-id">
                  {inventoryItem.amount}
                </TableCell>
                <TableCell align="center" data-cy="row-id">
                  {inventoryItem.serialNumber}
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    aria-label="locate"
                    size="small"
                    title="جایگذاری آیتم"
                    data-cy="locate"
                    onClick={() => props.locateItem(inventoryItem)}
                  >
                    <Icon
                      path={mdiClose}
                      color={theme.palette.error.main}
                      size={1}
                    />
                  </IconButton>
                </TableCell>
              </StyledTableRow>
            );
          })
        : null}
    </>
  );
}
